import { cva, type VariantProps } from "class-variance-authority";

import { typedObjectEntries } from "common/helpers/utils";

const defaultButtonClasses = [
  "transition-[background] duration-300 ease-in-out cursor-pointer rounded-full",
  "focus-visible:outline-none",
  "disabled:cursor-not-allowed disabled:bg-black-200 disabled:text-black-400 disabled:border-black-200",
];

const linkButtonClasses = [
  "transition-[color,border-color] duration-300 ease-in-out cursor-pointer py-0",
  "disabled:cursor-not-allowed disabled:text-black-400",
];

const linkVariants = ["link-black", "link-purple", "link-danger"] as const;
const nonLinkVariants = [
  "default-black",
  "default-purple",
  "secondary-black",
  "secondary-purple",
  "outline-black",
  "outline-danger",
  "outline-purple",
  "ghost-black",
  "ghost-purple",
  "ghost-danger",
  "gradient",
] as const;

const iconPaddingCompoundVariants = typedObjectEntries({
  hasLeftIcon: { xs: "pl-1", sm: "pl-2", md: "pl-2.5", lg: "pl-3" },
  hasRightIcon: { xs: "pr-1", sm: "pr-2", md: "pr-2.5", lg: "pr-3" },
}).reduce((accumulator, [key, sizesObject]) => {
  typedObjectEntries(sizesObject).forEach(([size, className]) => {
    accumulator.push({
      variant: [...nonLinkVariants],
      size,
      [key]: true,
      intent: "default",
      class: className,
    });
  });
  return accumulator;
}, []);

export const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center text-nowrap",
  {
    variants: {
      variant: {
        // Default
        "default-black": "",
        "default-purple": "bg-purple-500 text-white hover:bg-purple-400",

        // Secondary
        "secondary-black": "bg-black-100 text-black-950 hover:bg-black-200",
        "secondary-purple": "bg-purple-100 hover:bg-purple-200 text-purple-600",

        // Outline
        "outline-black":
          "border border-black-200 bg-white text-black-950 hover:bg-black-100",
        "outline-danger":
          "border border-red-200 bg-white text-red-500 hover:bg-red-50",
        "outline-purple":
          "border border-purple-200 bg-white text-purple-600 hover:bg-purple-50",

        // Ghost
        "ghost-black": "text-black-950 hover:bg-black-200",
        "ghost-purple": "text-purple-600 hover:bg-purple-100",
        "ghost-danger": "text-red-500 hover:bg-red-200",

        // Link - No border radius, different spacing, different disabled state
        "link-black": "text-black-950 hover:text-black-700",
        "link-purple": "text-purple-600 hover:text-purple-400",
        "link-danger": "text-black-500 hover:text-red-500",

        // Gradient Button (AI)
        gradient:
          "text-white transition-opacity bg-blackPurple disabled:bg-none hover:opacity-80 disabled:hover:opacity-100",
      },
      size: {
        xs: "h-6 min-w-6 gap-1 text-button-xs [&>svg]:size-4",
        sm: "h-8 min-w-8 gap-1 text-button-xs [&>svg]:size-4",
        md: "h-10 min-w-10 gap-2 text-button-sm [&>svg]:size-5",
        lg: "h-11 min-w-11 gap-2 text-button-md [&>svg]:size-6",
      },
      intent: {
        default: defaultButtonClasses,
        iconOnly: [...defaultButtonClasses, "p-0 rounded-full"],
        labelIcon: "pointer-events-none p-0 rounded-full",
      },
      hasLeftIcon: {
        true: null,
        false: null,
      },
      hasRightIcon: {
        true: null,
        false: null,
      },
      hasWhitelabelColor: {
        true: null,
        false: null,
      },
    },
    compoundVariants: [
      //base compound variants for non link buttons
      {
        variant: [...nonLinkVariants],
        size: "xs",
        intent: "default",
        class: "px-2 py-1",
      },
      {
        variant: [...nonLinkVariants],
        size: "sm",
        intent: "default",
        class: "px-4 py-2",
      },
      {
        variant: [...nonLinkVariants],
        size: "md",
        intent: "default",
        class: "px-5 py-2.5",
      },
      {
        variant: [...nonLinkVariants],
        size: "lg",
        intent: "default",
        class: "px-6 py-3",
      },
      //compound variants for non link buttons icons
      ...iconPaddingCompoundVariants,
      //compound variants for link buttons
      {
        variant: [...linkVariants],
        size: "sm",
        class: ["h-auto px-1 gap-1 text-xs-bold", linkButtonClasses],
      },
      {
        variant: [...linkVariants],
        size: "md",
        class: ["h-auto px-2.5 gap-1.5 text-sm-bold", linkButtonClasses],
      },
      {
        variant: [...linkVariants],
        size: "lg",
        class: ["h-auto px-3 gap-2 text-md-bold", linkButtonClasses],
      },
      //compound variants for link buttons icons
      {
        variant: [...linkVariants],
        hasLeftIcon: true,
        class: "pl-0",
      },
      {
        variant: [...linkVariants],
        hasRightIcon: true,
        class: "pr-0",
      },
      //default-black is styled differently based on whitelabel
      {
        variant: "default-black",
        class:
          "bg-[--whitelabel-color] hover:bg-[hsl(from_var(--whitelabel-color)_h_s_l/0.6)] text-[oklch(from_var(--whitelabel-color)_clamp(0,(l/0.7-1)*-infinity,1)_0_h/1)]",
        hasWhitelabelColor: true,
      },
      {
        variant: "default-black",
        class: "bg-black-950 text-white hover:bg-black-700",
        hasWhitelabelColor: false,
      },
    ],
  },
);

export type ButtonVariantType = VariantProps<typeof buttonVariants>["variant"];
